/**
 * CRA3D AI Training — curriculum landing page.
 *
 * Groups the 4 batches (Start Here, Month 1, Month 2, Month 3) into
 * section cards with each section's lessons + practice days. Shows
 * the user's current path recommendation if they have completed the
 * entry assessment, and offers a CTA to take / retake it otherwise.
 *
 * Backend contract: `GET /api/training/curriculum` returns
 *   { sections: [{id, label, lessons:[], practice_days:[]}],
 *     entry_assessment: {...} | null,
 *     shared_frameworks: {ask_check, guardrails},
 *     access_note }
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { extractApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  Sparkles, ArrowRight, Lock, FileText, Compass,
  CheckCircle2, ListChecks, BookOpen,
} from "lucide-react";


export default function Training() {
  const nav = useNavigate();
  const [state, setState] = useState({
    sections: [], entry_assessment: null,
    shared_frameworks: null, access_note: "",
  });
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  // Round-26: track partial failure separately so a transient
  // failure on ONE of the two parallel reads doesn't black-hole the
  // whole page. The user still sees whatever did load, plus a
  // precise error banner + retry affordance for the piece that didn't.
  const [curriculumError, setCurriculumError] = useState(null);
  const [programError, setProgramError] = useState(null);
  const [loadNonce, setLoadNonce] = useState(0);

  useEffect(() => {
    setLoading(true);
    setCurriculumError(null);
    setProgramError(null);
    let active = true;
    (async () => {
      const [curRes, progRes] = await Promise.allSettled([
        api.get("/training/curriculum"),
        api.get("/training/program"),
      ]);
      if (!active) return;
      if (curRes.status === "fulfilled") {
        setState({
          sections:          curRes.value.data?.sections || [],
          entry_assessment:  curRes.value.data?.entry_assessment || null,
          shared_frameworks: curRes.value.data?.shared_frameworks || null,
          access_note:       curRes.value.data?.access_note || "",
        });
      } else {
        setCurriculumError(extractApiError(
          curRes.reason,
          "Couldn't load the AI Training curriculum right now.",
        ));
      }
      if (progRes.status === "fulfilled") {
        setProgram(progRes.value.data || null);
      } else {
        setProgramError(extractApiError(
          progRes.reason,
          "Couldn't load your training program status right now.",
        ));
      }
      setLoading(false);
    })();
    return () => { active = false; };
  }, [loadNonce]);

  return (
    <AppShell>
      <div
        data-testid="training-page"
        className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12"
      >
        <div className="mb-6">
          <p className="text-xs uppercase tracking-wider text-fg-secondary">
            AI Training &middot; Preview
          </p>
          <h1
            data-testid="training-title"
            className="mt-1 text-3xl sm:text-4xl font-heading font-medium text-fg-primary leading-tight"
          >
            Practical AI skill lessons
          </h1>
          <p
            data-testid="training-subtitle"
            className="mt-3 max-w-2xl text-sm text-fg-secondary leading-relaxed"
          >
            Learn the AI basics at your own pace. Start Here lessons
            are free. Levels 1, 2, and 3 open with the matching
            training package or annual membership. See the{" "}
            <Link
              to="/plans"
              data-testid="training-plans-link"
              className="underline hover:text-fg-primary"
            >
              Plans page
            </Link>{" "}
            for pricing and the full access terms.
          </p>
        </div>

        <PathBanner
          entryAssessment={state.entry_assessment}
          onTake={() => nav("/training/assessment")}
        />

        {/* Round-27 — gate PurchaseStrip and AnnualOpenCard behind
            loading===false so neither renders during the initial
            fetch window. Previously the strip briefly flashed on
            screen before program data arrived (because
            `program === null` early-rendered the strip, then
            `program.active_annual === true` swapped it for the
            open-card). We now render a stable skeleton box
            during loading so the overall layout height does not
            jump, then swap in the correct card once account
            state is known. */}
        {loading && (
          <div
            data-testid="training-topcard-skeleton"
            className="mb-6 rounded-lg border border-border-subtle bg-bg-surface p-6 animate-pulse"
          >
            <div className="h-3 w-32 rounded bg-bg-surface-hover" />
            <div className="mt-3 h-6 w-72 rounded bg-bg-surface-hover" />
            <div className="mt-3 h-4 w-full max-w-lg rounded bg-bg-surface-hover" />
          </div>
        )}

        {!loading && program && program.active_annual && (
          <AnnualOpenCard />
        )}

        {!loading && !(program && program.active_annual) && (
          <PurchaseStrip
            program={program}
            onNavPlans={(slug) => nav(slug ? `/plans?intent_slug=${encodeURIComponent(slug)}` : "/plans")}
          />
        )}

        {curriculumError && (
          <Alert
            variant="destructive"
            data-testid="training-curriculum-error"
            className="mb-4"
          >
            <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
              <span>{curriculumError}</span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setLoadNonce((n) => n + 1)}
                data-testid="training-curriculum-retry-btn"
                className="border-border-subtle text-fg-primary hover:bg-bg-surface-hover"
              >
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        )}
        {programError && (
          <Alert
            variant="destructive"
            data-testid="training-program-error"
            className="mb-4"
          >
            <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
              <span>{programError}</span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setLoadNonce((n) => n + 1)}
                data-testid="training-program-retry-btn"
                className="border-border-subtle text-fg-primary hover:bg-bg-surface-hover"
              >
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {loading ? (
          <p
            data-testid="training-loading"
            className="text-sm text-fg-secondary text-center py-12"
          >
            Loading training lessons&hellip;
          </p>
        ) : (
          <>
            <div className="space-y-6" data-testid="training-sections">
              {state.sections.map((sec) => (
                <SectionCard
                  key={sec.id}
                  section={sec}
                  onOpenLesson={(id) => nav(`/training/lesson/${id}`)}
                  onOpenPracticeDay={(id) => nav(`/training/practice/${id}`)}
                  onUpgrade={(levelId) => nav(`/plans?gated_from=${levelId}`)}
                />
              ))}
            </div>
            {state.access_note && (
              <p
                data-testid="training-access-note"
                className="mt-6 text-xs text-fg-muted leading-relaxed"
              >
                {state.access_note}
              </p>
            )}
            {/* Round-13: annual-program 12-month timeline. Rendered
                only when the user holds an active annual membership
                grant. One-time package buyers and free users see the
                shorter "Months 4-12" note below instead. */}
            {program && program.active_annual && (
              <AnnualProgramTimeline program={program} />
            )}
            {/* Months 4-12 — honest "schedule only, content pending"
                copy for users WITHOUT an annual membership. Annual
                members see the full timeline above (which already
                covers Months 4-12 with per-month unlock dates). */}
            {!(program && program.active_annual) && (
              <section
                data-testid="training-upcoming-months"
                className="mt-8 rounded-lg border border-dashed border-border-subtle bg-bg-surface p-5"
              >
                <h2 className="text-sm font-heading font-medium text-fg-primary">
                  Months 4&ndash;12
                </h2>
                <p className="mt-2 text-sm text-fg-secondary leading-relaxed">
                  Nine additional themed months are part of the 12-month
                  annual-membership program. Annual members get all
                  twelve months available immediately on purchase and
                  can take them in any order. See the{" "}
                  <Link
                    to="/plans"
                    className="underline hover:text-fg-primary"
                    data-testid="training-upcoming-months-plans-link"
                  >
                    Plans page
                  </Link>{" "}
                  for annual-membership pricing and the full access
                  terms.
                </p>
              </section>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
}


/* ------------------------------------------------------------------
 * Round-26 — PurchaseStrip
 *
 * Four visible purchase boxes at the TOP of the AI Training page,
 * matching the four Round-26 owner-approved packages. Each box's
 * primary button routes to /plans so the final checkout step runs
 * through the existing /plans → Stripe TEST hosted-checkout flow —
 * we deliberately do NOT fork or duplicate the checkout initiation
 * code here, so the preserved payment-status guard, upgrade overlay,
 * and entitlement rules keep applying unchanged.
 *
 * For partial-package buyers (owns 30d or 60d), the strip highlights
 * the Annual box and shows a muted "Already yours" chip on the
 * package they own. The three one-time boxes stay reachable so a
 * 30d-owner can still see the 60d / 90d / Annual next steps.
 * ------------------------------------------------------------------ */
function PurchaseStrip({ program, onNavPlans }) {
  // Round-26: the "Already yours" chip is intentionally not shown
  // in this strip — the strip is only rendered when
  // program.active_annual is false, i.e. the user doesn't have the
  // dominant tier. Any one-time ownership they do have is already
  // disclosed on the /plans page (where the Buy / Already active
  // chip is driven from the full activeGrantSlugs set) so this
  // strip stays a lightweight discovery surface and avoids a
  // second round-trip to /api/plans just to label chips.
  const owns30 = false;
  const owns60 = false;
  const owns90 = false;
  return (
    <section
      data-testid="training-purchase-strip"
      className="mb-6"
    >
      <p
        data-testid="training-purchase-strip-eyebrow"
        className="text-[11px] uppercase tracking-widest text-fg-secondary"
      >
        AI Training &middot; purchase options
      </p>
      <h2
        data-testid="training-purchase-strip-heading"
        className="mt-1 text-xl sm:text-2xl font-heading font-medium text-fg-primary"
      >
        Pick the AI training that fits your pace.
      </h2>
      {/* Four purchase boxes. Grid: 1 col on mobile, 2 on sm, 4 on
          lg. The annual box spans the full width on mobile and sm
          so it stays visibly the major YELLOW offer, and takes 2
          columns on lg so it remains the dominant tile. */}
      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <PurchaseBox
          testid="training-purchase-box-30d"
          slug="training_30d"
          label="30-day package"
          price="$29.99"
          includes="Month 1"
          alreadyOwned={owns30}
          onClick={() => onNavPlans("training_30d")}
          tone="muted"
        />
        <PurchaseBox
          testid="training-purchase-box-60d"
          slug="training_60d"
          label="60-day package"
          price="$49.99"
          includes={"Months 1\u20132"}
          alreadyOwned={owns60}
          onClick={() => onNavPlans("training_60d")}
          tone="muted"
        />
        <PurchaseBox
          testid="training-purchase-box-90d"
          slug="training_90d"
          label="90-day package"
          price="$69.99"
          includes={"Months 1\u20133"}
          alreadyOwned={owns90}
          onClick={() => onNavPlans("training_90d")}
          tone="muted"
        />
        {/* Annual — the major YELLOW offer. Spans both columns on
            lg so it reads as the dominant tile. */}
        <div className="sm:col-span-2 lg:col-span-4">
          <PurchaseBox
            testid="training-purchase-box-annual"
            slug="training_membership_annual"
            label="Annual membership"
            price="$149.99"
            includes="Access all 12 months of AI Training content."
            alreadyOwned={false}
            onClick={() => onNavPlans("training_membership_annual")}
            tone="prominent"
            promo={{
              headline: "Get all 12 months of AI training.",
              lines: [
                "$149.99 for your first year.",
                "Access every month immediately. Learn in any order.",
                "Renews automatically at $169.99 per year.",
              ],
            }}
          />
        </div>
      </div>
      {/* Round-28 (2026-10-08): package-label clarification. The
          "30/60/90-day" names describe a suggested learning schedule;
          access does not expire by elapsed days. No price, offer, or
          access-rule change. */}
      <p
        data-testid="training-purchase-strip-label-note"
        className="mt-3 text-xs text-fg-secondary"
      >
        The &ldquo;30-day&rdquo;, &ldquo;60-day&rdquo;, and
        &ldquo;90-day&rdquo; names describe a suggested learning
        schedule. Months you have unlocked stay accessible for the
        full 12-month access period after purchase &mdash; lessons
        never lock because days elapsed.
      </p>
      <p
        data-testid="training-purchase-strip-plans-link"
        className="mt-3 text-xs text-fg-secondary"
      >
        Full renewal and cancellation terms, consent, and side-by-side
        package details:{" "}
        <Link
          to="/plans"
          className="underline hover:text-fg-primary"
          data-testid="training-purchase-strip-plans-anchor"
        >
          Plans &amp; Pricing
        </Link>
        .
      </p>
    </section>
  );
}


function PurchaseBox({ testid, slug, label, price, includes,
                       alreadyOwned, onClick, tone, promo }) {
  const prominent = tone === "prominent";
  return (
    <div
      data-testid={testid}
      data-slug={slug}
      data-tone={tone}
      className={
        "rounded-lg border p-4 sm:p-5 flex flex-col gap-3 " +
        (prominent
          ? "border-accent-warm bg-accent-warm/10 shadow-sm"
          : "border-border-subtle bg-bg-surface")
      }
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p
          data-testid={`${testid}-label`}
          className={
            "text-xs uppercase tracking-widest " +
            (prominent ? "text-accent-warm" : "text-fg-secondary")
          }
        >
          {label}
        </p>
        {alreadyOwned && (
          <span
            data-testid={`${testid}-owned-chip`}
            className="text-[10px] uppercase tracking-widest text-progress-teal border border-progress-teal/40 rounded px-1.5 py-0.5"
          >
            Already yours
          </span>
        )}
      </div>
      {promo ? (
        <div data-testid={`${testid}-promo`} className="space-y-2">
          <p
            data-testid={`${testid}-promo-headline`}
            className="text-xl sm:text-2xl font-heading font-medium text-fg-primary leading-snug"
          >
            {promo.headline}
          </p>
          <ul
            data-testid={`${testid}-promo-lines`}
            className="space-y-1 text-sm text-fg-primary"
          >
            {promo.lines.map((line, i) => (
              <li
                key={i}
                data-testid={`${testid}-promo-line-${i}`}
              >
                {i === 0 ? (
                  <strong className="text-accent-warm">{line}</strong>
                ) : (
                  line
                )}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="space-y-1">
          <p
            data-testid={`${testid}-price`}
            className="text-2xl font-heading font-medium text-fg-primary leading-tight"
          >
            {price}
          </p>
          <p
            data-testid={`${testid}-includes`}
            className="text-xs text-fg-secondary"
          >
            Includes {includes}
          </p>
        </div>
      )}
      <div className="mt-auto">
        <Button
          data-testid={`${testid}-cta`}
          onClick={onClick}
          size={prominent ? "lg" : "default"}
          className={
            "w-full " +
            (prominent
              ? "h-12 text-base bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
              : "bg-bg-surface-hover text-fg-primary hover:bg-bg-surface")
          }
        >
          {prominent
            ? `Join the 12-month program \u2014 ${price}`
            : alreadyOwned
              ? "Open on Plans"
              : `Buy ${label.toLowerCase()}`}
          <ArrowRight className="h-3.5 w-3.5 ml-2" aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
}


function AnnualOpenCard() {
  return (
    <section
      data-testid="training-annual-open-card"
      className="mb-6 rounded-lg border border-progress-teal/40 bg-progress-teal/5 p-5"
    >
      <div className="flex flex-wrap items-start gap-3">
        <CheckCircle2
          className="h-5 w-5 text-progress-teal flex-shrink-0 mt-0.5"
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          <p
            data-testid="training-annual-open-card-eyebrow"
            className="text-[11px] uppercase tracking-widest text-progress-teal"
          >
            AI Training &middot; annual membership active
          </p>
          <h2
            data-testid="training-annual-open-card-heading"
            className="mt-1 text-lg sm:text-xl font-heading font-medium text-fg-primary"
          >
            Open your training &mdash; all 12 months are ready.
          </h2>
          <p className="mt-1 text-sm text-fg-secondary leading-relaxed">
            Access every month immediately. Learn in any order. Jump
            back into the month you were working on, or pick a new
            one from the twelve-month overview below.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Link
              to="/training/lesson/m1-1-1"
              data-testid="training-annual-open-card-m1-cta"
              className="inline-flex items-center gap-1 text-sm px-3 py-2 rounded-md bg-accent-warm text-bg-base hover:bg-accent-warm-hover transition-colors duration-200"
            >
              Start Month 1
              <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
            </Link>
            <Link
              to="/settings"
              data-testid="training-annual-open-card-manage-link"
              className="text-xs underline text-fg-secondary hover:text-fg-primary"
            >
              Manage subscription in Settings
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}



function PathBanner({ entryAssessment, onTake }) {
  if (!entryAssessment) {
    return (
      <div
        data-testid="training-entry-assessment-cta"
        className="mb-6 rounded-lg border border-accent-warm/40 bg-accent-warm/5 p-5"
      >
        <div className="flex items-start gap-3">
          <Compass className="h-5 w-5 text-accent-warm flex-shrink-0 mt-0.5" aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-heading font-medium text-fg-primary">
              Find your starting point
            </h2>
            <p className="mt-1 text-sm text-fg-secondary leading-relaxed">
              Ten short questions. No test, no score &mdash; just a
              recommendation of which lessons to open first.
            </p>
            <Button
              data-testid="training-entry-assessment-start-btn"
              onClick={onTake}
              size="sm"
              className="mt-3 bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
            >
              Take the 10-question entry assessment
              <ArrowRight className="h-3.5 w-3.5 ml-2" aria-hidden="true" />
            </Button>
          </div>
        </div>
      </div>
    );
  }
  const pathLabel = {
    A: "Path A \u00b7 Start Here (modular)",
    B: "Path B \u00b7 Month 1",
    C: "Path C \u00b7 Month 1 (comfort-level)",
  }[entryAssessment.path] || `Path ${entryAssessment.path}`;
  return (
    <div
      data-testid="training-path-banner"
      className="mb-6 rounded-lg border border-accent-warm/40 bg-accent-warm/5 p-5"
    >
      <div className="flex items-start gap-3">
        <Compass className="h-5 w-5 text-accent-warm flex-shrink-0 mt-0.5" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Badge
              data-testid="training-path-banner-path"
              className="bg-accent-warm text-bg-base hover:bg-accent-warm uppercase text-[10px] tracking-widest"
            >
              {pathLabel}
            </Badge>
            {entryAssessment.fast_track && (
              <Badge
                data-testid="training-path-banner-fast-track"
                className="bg-transparent text-fg-primary hover:bg-transparent uppercase text-[10px] tracking-widest border border-accent-warm/60"
              >
                Fast-track suggested
              </Badge>
            )}
          </div>
          <p className="mt-2 text-sm text-fg-primary leading-relaxed">
            Your recommended first lessons:
          </p>
          <ul className="mt-2 flex flex-wrap gap-2">
            {(entryAssessment.recommended_lesson_ids || []).map((id) => (
              <li key={id}>
                <Link
                  to={`/training/lesson/${id}`}
                  data-testid={`training-path-banner-open-${id}`}
                  className="inline-flex items-center gap-1 rounded-full border border-accent-warm/40 bg-bg-surface px-3 py-1 text-xs font-mono text-fg-primary hover:bg-accent-warm/10"
                >
                  {id}
                  <ArrowRight className="h-3 w-3" aria-hidden="true" />
                </Link>
              </li>
            ))}
          </ul>
          <Link
            to="/training/assessment"
            data-testid="training-path-banner-retake-link"
            className="mt-3 inline-block text-xs underline text-fg-secondary hover:text-fg-primary"
          >
            Retake the assessment
          </Link>
        </div>
      </div>
    </div>
  );
}


function SectionCard({ section, onOpenLesson, onOpenPracticeDay, onUpgrade }) {
  const completedCount = (section.lessons || []).filter((l) => l.completed).length
    + (section.practice_days || []).filter((d) => d.completed).length;
  const total = (section.lessons || []).length + (section.practice_days || []).length;
  return (
    <section
      data-testid={`training-section-${section.id}`}
      className="rounded-lg border border-border-subtle bg-bg-surface p-5"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border-subtle pb-3 mb-4">
        <h2
          data-testid={`training-section-${section.id}-label`}
          className="text-lg font-heading font-medium text-fg-primary"
        >
          {section.label}
        </h2>
        <span
          data-testid={`training-section-${section.id}-progress`}
          className="text-xs font-mono text-fg-muted"
        >
          {completedCount}/{total} marked complete
        </span>
      </div>
      <ul className="space-y-2">
        {(section.lessons || []).map((lesson) => (
          <LessonRow
            key={lesson.id}
            lesson={lesson}
            onOpen={() => onOpenLesson(lesson.id)}
            onUpgrade={() => onUpgrade(lesson.level_id)}
          />
        ))}
        {(section.practice_days || []).map((day) => (
          <PracticeDayRow
            key={day.id}
            day={day}
            onOpen={() => onOpenPracticeDay(day.id)}
          />
        ))}
      </ul>
    </section>
  );
}


function LessonRow({ lesson, onOpen, onUpgrade }) {
  const isSample = lesson.is_free_sample;
  const locked = !lesson.can_open && !isSample;
  return (
    <li
      data-testid={`training-lesson-row-${lesson.id}`}
      data-can-open={lesson.can_open ? "true" : "false"}
      data-completed={lesson.completed ? "true" : "false"}
      className={
        "flex flex-wrap items-start gap-3 rounded-md border px-3 py-2.5 " +
        (isSample
          ? "border-accent-warm/40 bg-accent-warm/5"
          : "border-border-subtle bg-bg-base")
      }
    >
      <span className={"inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md " +
        (isSample ? "bg-accent-warm/20 text-accent-warm" : "bg-bg-surface-hover text-fg-secondary")}>
        {lesson.completed ? (
          <CheckCircle2 className="h-4 w-4 text-accent-warm" aria-hidden="true" />
        ) : isSample ? (
          <Sparkles className="h-4 w-4" aria-hidden="true" />
        ) : (
          <BookOpen className="h-4 w-4" aria-hidden="true" />
        )}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <code className="text-xs font-mono text-fg-muted">{lesson.id}</code>
          {isSample && (
            <Badge
              data-testid={`training-lesson-row-${lesson.id}-free-badge`}
              className="bg-accent-warm text-bg-base hover:bg-accent-warm uppercase text-[10px] tracking-widest"
            >
              Free sample
            </Badge>
          )}
          {locked && (
            <Badge
              data-testid={`training-lesson-row-${lesson.id}-locked-badge`}
              className="bg-transparent text-fg-muted hover:bg-transparent uppercase text-[10px] tracking-widest border border-fg-muted/40"
            >
              <Lock className="h-3 w-3 mr-1" aria-hidden="true" /> Paid
            </Badge>
          )}
          {lesson.completed && (
            <Badge
              data-testid={`training-lesson-row-${lesson.id}-completed-badge`}
              className="bg-transparent text-fg-primary hover:bg-transparent uppercase text-[10px] tracking-widest border border-accent-warm/60"
            >
              Completed
            </Badge>
          )}
        </div>
        <p
          data-testid={`training-lesson-row-${lesson.id}-title`}
          className="mt-1 text-sm font-medium text-fg-primary leading-snug"
        >
          {lesson.title}
        </p>
        {lesson.outcome && (
          <p className="mt-0.5 text-xs text-fg-secondary leading-relaxed">
            {lesson.outcome}
          </p>
        )}
      </div>
      <div className="flex-shrink-0">
        {locked ? (
          <Button
            data-testid={`training-lesson-row-${lesson.id}-upgrade-btn`}
            size="sm"
            variant="outline"
            onClick={onUpgrade}
            className="border-border-subtle text-fg-primary hover:bg-bg-surface-hover"
          >
            See packages
          </Button>
        ) : (
          <Button
            data-testid={`training-lesson-row-${lesson.id}-open-btn`}
            size="sm"
            onClick={onOpen}
            className={isSample
              ? "bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
              : "bg-bg-surface-hover text-fg-primary hover:bg-bg-surface"}
          >
            Open
            <ArrowRight className="h-3.5 w-3.5 ml-2" aria-hidden="true" />
          </Button>
        )}
      </div>
    </li>
  );
}


function PracticeDayRow({ day, onOpen }) {
  return (
    <li
      data-testid={`training-practice-row-${day.id}`}
      data-completed={day.completed ? "true" : "false"}
      className="flex flex-wrap items-start gap-3 rounded-md border border-dashed border-border-subtle bg-bg-base px-3 py-2.5"
    >
      <span className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-bg-surface-hover text-fg-secondary">
        {day.completed ? (
          <CheckCircle2 className="h-4 w-4 text-accent-warm" aria-hidden="true" />
        ) : (
          <ListChecks className="h-4 w-4" aria-hidden="true" />
        )}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <code className="text-xs font-mono text-fg-muted">{day.id}</code>
          {day.is_skill_check && (
            <Badge
              data-testid={`training-practice-row-${day.id}-skill-check-badge`}
              className="bg-accent-warm text-bg-base hover:bg-accent-warm uppercase text-[10px] tracking-widest"
            >
              Skill check
            </Badge>
          )}
          {day.completed && (
            <Badge
              data-testid={`training-practice-row-${day.id}-completed-badge`}
              className="bg-transparent text-fg-primary hover:bg-transparent uppercase text-[10px] tracking-widest border border-accent-warm/60"
            >
              Completed
            </Badge>
          )}
        </div>
        <p
          data-testid={`training-practice-row-${day.id}-title`}
          className="mt-1 text-sm font-medium text-fg-primary leading-snug"
        >
          {day.title}
        </p>
      </div>
      <div className="flex-shrink-0">
        <Button
          data-testid={`training-practice-row-${day.id}-open-btn`}
          size="sm"
          variant="outline"
          onClick={onOpen}
          className="border-border-subtle text-fg-primary hover:bg-bg-surface-hover"
        >
          Open checklist
          <ArrowRight className="h-3.5 w-3.5 ml-2" aria-hidden="true" />
        </Button>
      </div>
    </li>
  );
}


/**
 * Round-13: 12-month member-relative program timeline.
 *
 * Props: `program` = response from GET /api/training/program.
 *   Expected shape:
 *     { active_annual: true,
 *       start_at: ISO,
 *       test_backdated: bool,
 *       server_now: ISO,
 *       months: [{month, title, description, is_published,
 *                 unlocks_at, unlocked, accessible, lock_reason}] }
 *
 * Rendering rules:
 *   - Accessible months get a solid "Open" affordance styled like
 *     the Section cards (routes to the matching curriculum section).
 *   - Unlocked-but-not-published months show "Content pending — your
 *     schedule is ready" with the lock_reason.
 *   - Locked months show a date + "Unlocks in Month N of your
 *     program".
 *   - No internal construction-state wording reaches the customer
 *     (locked is a schedule state, not a construction state).
 */
function AnnualProgramTimeline({ program }) {
  const months = program?.months || [];
  const startAt = program?.start_at ? new Date(program.start_at) : null;
  return (
    <section
      data-testid="training-annual-program"
      className="mt-8 rounded-lg border border-accent-warm/40 bg-accent-warm/5 p-5"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-widest text-progress-teal">
            Annual membership &middot; 12-month program
          </p>
          <h2
            data-testid="training-annual-program-heading"
            className="mt-1 text-lg sm:text-xl font-heading font-medium text-fg-primary"
          >
            Your program timeline
          </h2>
          {startAt && (
            <p
              data-testid="training-annual-program-start"
              className="mt-1 text-xs text-fg-secondary"
            >
              Program start:{" "}
              <strong className="text-fg-primary">
                {startAt.toISOString().slice(0, 10)}
              </strong>{" "}
              (UTC) &mdash; access every month immediately. Learn in
              any order.
            </p>
          )}
          {program?.test_backdated && (
            <p
              data-testid="training-annual-program-synthetic-notice"
              className="mt-2 text-xs italic text-fg-muted"
            >
              Preview note: this program start was synthetically
              adjusted by an administrator for testing. Your real
              purchase anchor is preserved on the grant.
            </p>
          )}
        </div>
      </div>
      <ol
        data-testid="training-annual-program-months"
        className="mt-4 space-y-2"
      >
        {months.map((m) => (
          <ProgramMonthRow key={m.month} month={m} />
        ))}
      </ol>
    </section>
  );
}


function ProgramMonthRow({ month }) {
  const unlockDate = month.unlocks_at
    ? month.unlocks_at.slice(0, 10)
    : null;
  const chipState = month.accessible
    ? "accessible"
    : month.unlocked
      ? "pending-content"
      : "locked";
  const chipLabel = {
    "accessible":      "Available now",
    "pending-content": "Schedule ready \u00b7 content pending",
    "locked":          `Unlocks ${unlockDate || ""}`,
  }[chipState];
  const chipClass = {
    "accessible":      "border border-progress-teal/60 text-progress-teal",
    "pending-content": "border border-accent-warm/60 text-accent-warm",
    "locked":          "border border-border-subtle text-fg-secondary",
  }[chipState];
  const icon = month.accessible
    ? <CheckCircle2 className="h-4 w-4 text-progress-teal" aria-hidden="true" />
    : <Lock className="h-4 w-4 text-fg-muted" aria-hidden="true" />;
  return (
    <li
      data-testid={`training-annual-program-month-${month.month}`}
      data-accessible={month.accessible ? "true" : "false"}
      data-unlocked={month.unlocked ? "true" : "false"}
      className="flex flex-wrap items-start gap-3 rounded-md border border-border-subtle bg-bg-surface px-3 py-2.5"
    >
      <span className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-bg-base">
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <code
            data-testid={`training-annual-program-month-${month.month}-label`}
            className="text-xs font-mono text-fg-muted"
          >
            Month {month.month}
          </code>
          <Badge
            data-testid={`training-annual-program-month-${month.month}-chip`}
            className={`${chipClass} bg-transparent uppercase text-[10px] tracking-widest hover:bg-transparent`}
          >
            {chipLabel}
          </Badge>
        </div>
        <p
          data-testid={`training-annual-program-month-${month.month}-title`}
          className="mt-1 text-sm font-medium text-fg-primary leading-snug"
        >
          {month.title}
        </p>
        <p
          data-testid={`training-annual-program-month-${month.month}-description`}
          className="mt-1 text-xs text-fg-secondary leading-relaxed"
        >
          {month.description}
        </p>
        {!month.accessible && month.lock_reason && (
          <p
            data-testid={`training-annual-program-month-${month.month}-reason`}
            className="mt-2 text-xs italic text-fg-muted leading-relaxed"
          >
            {month.lock_reason}
          </p>
        )}
      </div>
    </li>
  );
}
